// The storefront render path makes three blocking calls to traveler-api:
// agency resolution, experience-by-slug, and live availability. A bare
// `fetch` has no deadline and no retry, so a single transient blip — a 5xx
// during a cold start, a dropped connection — propagates straight up and
// crashes the whole detail page via the route error boundary. A blip that a
// retry would clear should never take the page down.

export interface RetryOptions {
  /** Extra attempts after the first. Total tries = retries + 1. */
  retries?: number;
  /** Per-attempt deadline. Each attempt gets a fresh `AbortSignal.timeout`. */
  timeoutMs?: number;
  /** Base backoff between attempts, doubled each retry (200, 400, 800…). */
  backoffMs?: number;
}

const DEFAULTS: Required<RetryOptions> = {
  retries: 2,
  timeoutMs: 6_000,
  backoffMs: 200,
};

/**
 * Runs `attempt` with a per-attempt timeout signal, retrying transient
 * failures with exponential backoff.
 *
 * A failure is transient when the attempt throws (network error or the
 * deadline aborting the signal) OR returns a result that `isTransient`
 * flags (e.g. an HTTP 5xx). A non-transient result is returned immediately.
 *
 * After exhausting retries: if any attempt produced a result it is returned
 * (the caller maps its own error shape from it); if every attempt threw, the
 * last error is rethrown.
 */
export async function withRetry<T>(
  attempt: (signal: AbortSignal) => Promise<T>,
  isTransient: (result: T) => boolean,
  options: RetryOptions = {},
): Promise<T> {
  const { retries, timeoutMs, backoffMs } = { ...DEFAULTS, ...options };

  let lastError: unknown;
  let lastResult: T | undefined;
  let haveResult = false;

  for (let i = 0; i <= retries; i++) {
    try {
      const result = await attempt(AbortSignal.timeout(timeoutMs));
      if (!isTransient(result)) return result;
      lastResult = result;
      haveResult = true;
    } catch (err) {
      lastError = err;
    }
    if (i < retries) await delay(backoffMs * 2 ** i);
  }

  if (haveResult) return lastResult as T;
  throw lastError;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
